import { z } from "zod";

// The athlete's own layout of a widget surface: one order list (shown), one
// hidden list and one collapsed list, all of widget ids. Home (HR-11) and
// Progress (PG-57) are the two surfaces; each passes its own config.
// Absent from the profile means the surface's default layout.

/** What a surface declares about its widgets. */
export type WidgetSurface<Id extends string> = {
  /** Every widget, in the default order. */
  ids: readonly Id[];
  /** Never hidden, always first. */
  pinned: readonly Id[];
  /** Never hidden, but moved like any other widget. */
  unhideable: readonly Id[];
  /** Off until the athlete turns them on: a layout that has never seen one
   *  of these lists it as hidden. */
  defaultHidden: readonly Id[];
  /** Shown closed until the athlete opens them in the config. */
  defaultCollapsed: readonly Id[];
};

// Plain strings, not an id enum: a profile written by a newer build, with a
// widget this build does not know yet, must still parse.
const StoredWidgetId = z.string().min(1).max(40);
export const WidgetLayoutSchema = z.object({
  order: z.array(StoredWidgetId).max(40),
  hidden: z.array(StoredWidgetId).max(40),
  // PG-57: additive. A layout saved before it existed stays valid.
  collapsed: z.array(StoredWidgetId).max(40).optional(),
});
export type StoredWidgetLayout = z.infer<typeof WidgetLayoutSchema>;

/** A raw profile value to a stored layout; anything malformed reads as
 *  absent, i.e. the default layout. */
export function parseStoredWidgetLayout(
  raw: unknown,
): StoredWidgetLayout | undefined {
  const parsed = WidgetLayoutSchema.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}

/** A resolved layout: every known widget exactly once, in `order` (shown) or
 *  in `hidden`, with the pinned ones leading `order`. `collapsed` says which
 *  of them render closed, shown or not. */
export type WidgetLayout<Id extends string> = {
  order: Id[];
  hidden: Id[];
  collapsed: Id[];
};

const isKnown = <Id extends string>(s: WidgetSurface<Id>, v: string): v is Id =>
  (s.ids as readonly string[]).includes(v);

export const isWidgetPinned = <Id extends string>(
  s: WidgetSurface<Id>,
  id: Id,
): boolean => s.pinned.includes(id);

/** Pinned and unhideable widgets never go to the hidden list. */
export const isWidgetHideable = <Id extends string>(
  s: WidgetSurface<Id>,
  id: Id,
): boolean => !isWidgetPinned(s, id) && !s.unhideable.includes(id);

/** Stored (or absent) layout to a complete one. Unknown ids are dropped; a
 *  widget the layout has never seen lands after its nearest default-order
 *  predecessor, so adding a widget never needs a migration. A never-seen
 *  default-hidden widget goes to the end of `hidden` instead, and an
 *  unhideable one listed as hidden is shown. A never-seen default-collapsed
 *  widget starts collapsed. */
export function resolveWidgetLayout<Id extends string>(
  s: WidgetSurface<Id>,
  stored?: StoredWidgetLayout | null,
): WidgetLayout<Id> {
  const seen = new Set<Id>();
  const hidden: Id[] = [];
  for (const v of stored?.hidden ?? []) {
    if (!isKnown(s, v) || !isWidgetHideable(s, v) || seen.has(v)) continue;
    seen.add(v);
    hidden.push(v);
  }
  const order: Id[] = [];
  for (const v of stored?.order ?? []) {
    if (!isKnown(s, v) || seen.has(v)) continue;
    seen.add(v);
    order.push(v);
  }
  const collapsed = new Set<Id>(
    (stored?.collapsed ?? []).filter((v): v is Id => isKnown(s, v)),
  );
  s.ids.forEach((id, i) => {
    if (seen.has(id)) return;
    if (s.defaultCollapsed.includes(id)) collapsed.add(id);
    if (s.defaultHidden.includes(id)) {
      hidden.push(id);
      return;
    }
    let at = 0;
    for (let j = i - 1; j >= 0; j--) {
      const k = order.indexOf(s.ids[j]!);
      if (k !== -1) {
        at = k + 1;
        break;
      }
    }
    order.splice(at, 0, id);
  });
  const pinned = s.ids.filter((id) => isWidgetPinned(s, id));
  return {
    order: [...pinned, ...order.filter((id) => !isWidgetPinned(s, id))],
    hidden,
    collapsed: s.ids.filter((id) => collapsed.has(id)),
  };
}

/** Move a shown widget to `toIndex` of `order`. Pinned widgets do not move and
 *  nothing moves above them. */
export function moveWidget<Id extends string>(
  s: WidgetSurface<Id>,
  layout: WidgetLayout<Id>,
  id: Id,
  toIndex: number,
): WidgetLayout<Id> {
  const from = layout.order.indexOf(id);
  if (from === -1 || isWidgetPinned(s, id)) return layout;
  const floor = layout.order.filter((x) => isWidgetPinned(s, x)).length;
  const to = Math.max(floor, Math.min(layout.order.length - 1, toIndex));
  if (to === from) return layout;
  const order = layout.order.filter((x) => x !== id);
  order.splice(to, 0, id);
  return { ...layout, order };
}

/** Hide a shown widget: it goes to the END of the hidden list. Pinned and
 *  unhideable widgets stay. */
export function hideWidget<Id extends string>(
  s: WidgetSurface<Id>,
  layout: WidgetLayout<Id>,
  id: Id,
): WidgetLayout<Id> {
  if (!isWidgetHideable(s, id) || !layout.order.includes(id)) return layout;
  return {
    ...layout,
    order: layout.order.filter((x) => x !== id),
    hidden: [...layout.hidden, id],
  };
}

/** Show a hidden widget again: it goes to the END of the shown list. */
export function showWidget<Id extends string>(
  layout: WidgetLayout<Id>,
  id: Id,
): WidgetLayout<Id> {
  if (!layout.hidden.includes(id)) return layout;
  return {
    ...layout,
    order: [...layout.order, id],
    hidden: layout.hidden.filter((x) => x !== id),
  };
}

/** Open or close a widget. The collapsed list keeps the surface's order. */
export function setWidgetCollapsed<Id extends string>(
  s: WidgetSurface<Id>,
  layout: WidgetLayout<Id>,
  id: Id,
  collapsed: boolean,
): WidgetLayout<Id> {
  if (layout.collapsed.includes(id) === collapsed) return layout;
  const next = new Set(layout.collapsed);
  if (collapsed) next.add(id);
  else next.delete(id);
  return { ...layout, collapsed: s.ids.filter((x) => next.has(x)) };
}

export function sameWidgetLayout<Id extends string>(
  a: WidgetLayout<Id>,
  b: WidgetLayout<Id>,
): boolean {
  const same = (x: Id[], y: Id[]) =>
    x.length === y.length && x.every((v, i) => v === y[i]);
  return (
    same(a.order, b.order) &&
    same(a.hidden, b.hidden) &&
    same(a.collapsed, b.collapsed)
  );
}

/** What goes to the profile. A surface that never collapses (Home) stores
 *  the same shape it did before the field existed. */
export function toStoredWidgetLayout<Id extends string>(
  layout: WidgetLayout<Id>,
): StoredWidgetLayout {
  const { order, hidden, collapsed } = layout;
  return collapsed.length > 0 ? { order, hidden, collapsed } : { order, hidden };
}
